import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Header from "../../components/Header";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  CheckCircle, X, IdentificationCard, Clock, Copy,
  MagnifyingGlass, Trash, Printer,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

/**
 * AgentApplicationsAdmin — admin review queue for landing-page agent applicants.
 *
 * Surfaces:
 *   • Tabs: pending / approved / rejected (counts inline)
 *   • Search by name / email / phone / DL number / address (client-side, live)
 *   • Click email → opens full applicant info panel (same dialog as "Review")
 *   • Per-row actions: Review, Print (opens print-formatted layout), Delete
 *     (non-approved only — guarded server-side too)
 *   • Approve creates the agent + dispatches welcome email
 *   • Reject captures reason + records reviewer
 */
export default function AgentApplicationsAdmin() {
  const [tab, setTab] = useState("pending");
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null); // app object to confirm deletion
  // Bulk selection — Set of app ids currently checked. Auto-cleared on tab
  // change so a row checked on "pending" doesn't survive into "approved".
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/admin/agent-applications?status=${tab}`);
      setApps(r.data.applications || []);
      setCounts(r.data.counts || {});
    } catch {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  // Clear bulk selection whenever the tab changes — selections only make
  // sense within a single status pool.
  useEffect(() => { setSelectedIds(new Set()); }, [tab]);

  const toggleOne = (appId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId); else next.add(appId);
      return next;
    });
  };

  const toggleAllVisible = (visibleApps) => {
    setSelectedIds(prev => {
      // Only select rows that can actually be acted on (pending only). On
      // any other tab the checkbox column is hidden anyway.
      const actionable = visibleApps.filter(a => a.status === "pending").map(a => a.id);
      const allSelected = actionable.length > 0 && actionable.every(id => prev.has(id));
      if (allSelected) {
        // Deselect all visible
        const next = new Set(prev);
        actionable.forEach(id => next.delete(id));
        return next;
      }
      // Select all visible (preserving any non-visible selections)
      const next = new Set(prev);
      actionable.forEach(id => next.add(id));
      return next;
    });
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/agent-applications/bulk-approve", {
        app_ids: Array.from(selectedIds),
        commission_percent: 0.5,
      });
      const ok = r.data?.approved_count ?? 0;
      const fail = r.data?.failed_count ?? 0;
      if (ok > 0 && fail === 0) {
        toast.success(`Approved ${ok} application${ok === 1 ? "" : "s"}`);
      } else if (ok > 0 && fail > 0) {
        toast.warning(`Approved ${ok}, ${fail} failed — check Rejected/Pending tabs.`);
      } else {
        toast.error(`All ${fail} approvals failed`);
      }
      setSelectedIds(new Set());
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0) return;
    const reason = bulkRejectReason.trim();
    if (reason.length < 2) {
      toast.error("Rejection reason is required (min 2 chars)");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/agent-applications/bulk-reject", {
        app_ids: Array.from(selectedIds),
        reason,
      });
      const ok = r.data?.rejected_count ?? 0;
      const fail = r.data?.failed_count ?? 0;
      if (ok > 0 && fail === 0) {
        toast.success(`Rejected ${ok} application${ok === 1 ? "" : "s"}`);
      } else if (ok > 0 && fail > 0) {
        toast.warning(`Rejected ${ok}, ${fail} failed`);
      } else {
        toast.error(`All ${fail} rejections failed`);
      }
      setSelectedIds(new Set());
      setBulkRejectOpen(false);
      setBulkRejectReason("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk reject failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return apps;
    return apps.filter(a =>
      (a.full_name || "").toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s) ||
      (a.phone || "").toLowerCase().includes(s) ||
      (a.dl_number || "").toLowerCase().includes(s) ||
      (a.address || "").toLowerCase().includes(s)
    );
  }, [apps, search]);

  const handlePrint = (app) => {
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups for this site"); return; }
    w.document.write(renderPrintHTML(app));
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* user cancelled */ } }, 350);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/agent-applications/${deleting.id}`);
      toast.success(`Application from ${deleting.full_name} deleted`);
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Admin</p>
        <h1 className="serif text-5xl mt-2">Agent applications</h1>
        <p className="text-[#4A5C50] mt-2 max-w-2xl">
          Public landing-page applications. Review the applicant&apos;s driver license, then approve (auto-creates an agent
          account with a one-time temp password) or reject with a reason.
          Click any <span className="text-[#184A30] font-semibold underline">email</span> to view full applicant info.
        </p>

        <div className="flex flex-wrap items-center gap-3 mt-8 justify-between">
          <div className="flex gap-2" data-testid="apps-tab-bar">
            {["pending", "approved", "rejected"].map(s => (
              <button
                key={s}
                onClick={() => { setTab(s); setLoading(true); }}
                className={`text-xs tracking-[0.2em] uppercase font-semibold px-4 py-2 rounded-full transition ${
                  tab === s ? "bg-[#184A30] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5C50] hover:bg-stone-50"
                }`}
                data-testid={`apps-tab-${s}`}
              >
                {s} ({counts[s] ?? 0})
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="pl-9 pr-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#184A30]"
              data-testid="apps-search"
            />
          </div>
        </div>

        {tab === "pending" && selectedIds.size > 0 && (
          <div
            className="sticky top-[80px] z-30 mt-4 bg-[#184A30] text-white rounded-2xl px-5 py-3 flex items-center justify-between gap-3 shadow-lg shadow-[#184A30]/20"
            data-testid="bulk-actions-bar"
          >
            <div className="text-sm">
              <span className="font-semibold" data-testid="bulk-selection-count">{selectedIds.size}</span> application
              {selectedIds.size === 1 ? "" : "s"} selected
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="ml-3 text-xs text-white/70 hover:text-white underline underline-offset-2"
                data-testid="bulk-clear-selection"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleBulkApprove}
                disabled={bulkBusy}
                className="bg-emerald-500 hover:bg-emerald-600 text-white border-0"
                data-testid="bulk-approve-btn"
              >
                <CheckCircle size={14} className="mr-1" />
                {bulkBusy ? "Approving…" : `Approve ${selectedIds.size}`}
              </Button>
              <Button
                size="sm"
                onClick={() => setBulkRejectOpen(true)}
                disabled={bulkBusy}
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                data-testid="bulk-reject-btn"
              >
                <X size={14} className="mr-1" />
                Reject {selectedIds.size}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-[#4A5C50]">Loading…</div>
        ) : (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl mt-6 overflow-x-auto" data-testid="apps-table">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-[#4A5C50]" data-testid="apps-empty">
                <Clock size={28} className="mx-auto mb-3 text-[#8BA888]" />
                {search ? `No ${tab} applications match "${search}"` : `No ${tab} applications.`}
              </div>
            ) : (
              <>
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
                    <tr>
                      {tab === "pending" && (
                        <th className="p-4 w-10">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                            checked={filtered.length > 0 && filtered.every(a => selectedIds.has(a.id))}
                            ref={(el) => {
                              if (el) {
                                const some = filtered.some(a => selectedIds.has(a.id));
                                const all = filtered.length > 0 && filtered.every(a => selectedIds.has(a.id));
                                el.indeterminate = some && !all;
                              }
                            }}
                            onChange={() => toggleAllVisible(filtered)}
                            data-testid="apps-select-all"
                            title="Select all visible"
                          />
                        </th>
                      )}
                      <th className="p-4">Submitted</th>
                      <th className="p-4">Applicant</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Phone</th>
                      <th className="p-4">DL #</th>
                      <th className="p-4">SSN</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(a => (
                      <tr key={a.id} className={`border-t border-[#E5E0D8] hover:bg-stone-50 ${selectedIds.has(a.id) ? "bg-[#CBA35C]/10" : ""}`} data-testid={`app-row-${a.id}`}>
                        {tab === "pending" && (
                          <td className="p-4">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                              checked={selectedIds.has(a.id)}
                              onChange={() => toggleOne(a.id)}
                              data-testid={`app-checkbox-${a.id}`}
                              aria-label={`Select ${a.full_name}`}
                            />
                          </td>
                        )}
                        <td className="p-4 text-xs text-[#4A5C50]">{new Date(a.created_at).toLocaleString()}</td>
                        <td className="p-4">
                          <div className="font-semibold text-[#112318]">{a.full_name}</div>
                          <div className="text-[11px] text-[#4A5C50] mt-0.5 truncate max-w-[220px]">{a.address || "—"}</div>
                        </td>
                        <td className="p-4">
                          <button
                            type="button"
                            onClick={() => setSelected(a)}
                            className="mono text-[#184A30] font-semibold underline decoration-dotted decoration-[#184A30] underline-offset-2 hover:text-[#0F3320] hover:decoration-solid focus:outline-none focus:ring-2 focus:ring-[#184A30] rounded px-1 -mx-1"
                            data-testid={`app-email-link-${a.id}`}
                            title="View full applicant info"
                          >
                            {a.email}
                          </button>
                        </td>
                        <td className="p-4 mono text-xs">{a.phone}</td>
                        <td className="p-4 mono text-xs">{a.dl_number}</td>
                        <td className="p-4 mono text-xs text-[#4A5C50]" data-testid={`app-ssn-masked-${a.id}`}>
                          {a.ssn_last4 ? `•••-••-${a.ssn_last4}` : "—"}
                        </td>
                        <td className="p-4">
                          <StatusPill status={a.status} />
                          {a.rejection_reason && (
                            <div className="text-xs text-rose-700 mt-1 max-w-[220px] truncate" title={a.rejection_reason}>
                              {a.rejection_reason}
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => setSelected(a)} data-testid={`app-review-${a.id}`}>
                              Review
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => handlePrint(a)}
                              data-testid={`app-print-${a.id}`}
                              title="Print applicant info"
                            >
                              <Printer size={14} />
                            </Button>
                            {a.status !== "approved" && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setDeleting(a)}
                                className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                data-testid={`app-delete-${a.id}`}
                                title="Delete application"
                              >
                                <Trash size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 border-t border-[#E5E0D8] bg-stone-50 text-xs text-[#4A5C50] flex justify-between" data-testid="apps-footer">
                  <span>{filtered.length} application{filtered.length === 1 ? "" : "s"}{search ? ` matching "${search}"` : ""}</span>
                  <span className="mono">{tab} tab</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {selected && (
        <ApplicationReviewDialog
          app={selected}
          onClose={() => setSelected(null)}
          onChanged={refresh}
          onPrint={() => handlePrint(selected)}
        />
      )}

      {deleting && (
        <DeleteConfirmDialog
          app={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={handleDelete}
        />
      )}

      <Dialog open={bulkRejectOpen} onOpenChange={(v) => { if (!v && !bulkBusy) { setBulkRejectOpen(false); setBulkRejectReason(""); } }}>
        <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8]" data-testid="bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl text-rose-700">Reject {selectedIds.size} application{selectedIds.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription className="text-[#4A5C50]">
              Each applicant will be notified by email and (if they opted in) SMS with the reason below.
              Their pending login records will be removed so they can re-apply later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulk-reject-reason" className="text-xs uppercase tracking-widest text-[#4A5C50]">Shared rejection reason</Label>
            <textarea
              id="bulk-reject-reason"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="E.g. Driver license photo unclear — please re-apply with clearer images."
              className="w-full min-h-[100px] rounded-lg border border-[#E5E0D8] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
              maxLength={500}
              data-testid="bulk-reject-reason-input"
            />
            <p className="text-[10px] text-[#4A5C50]">{bulkRejectReason.length}/500</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" disabled={bulkBusy} onClick={() => { setBulkRejectOpen(false); setBulkRejectReason(""); }}>Cancel</Button>
            <Button
              onClick={handleBulkReject}
              disabled={bulkBusy || bulkRejectReason.trim().length < 2}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-reject-confirm-btn"
            >
              {bulkBusy ? "Rejecting…" : `Reject ${selectedIds.size} application${selectedIds.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "bg-amber-100 text-amber-800", label: "Pending" },
    approved: { c: "bg-emerald-100 text-emerald-700", label: "Approved" },
    rejected: { c: "bg-rose-100 text-rose-700", label: "Rejected" },
  };
  const m = map[status] || { c: "bg-stone-100 text-stone-700", label: status };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] tracking-widest uppercase font-semibold ${m.c}`}>{m.label}</span>;
}

function DeleteConfirmDialog({ app, onCancel, onConfirm }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8]" data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-rose-700">Delete application?</DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            This will permanently remove <strong>{app.full_name}</strong> ({app.email})&apos;s submission and their
            driver license photos from storage. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm-btn">
            <Trash size={14} className="mr-1" /> Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicationReviewDialog({ app, onClose, onChanged, onPrint }) {
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState({
    bank_account_holder: app.full_name, bank_account_number: "", bank_name: "", routing_number: "",
    commission_percent: 0.5, notes: "",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [approvalResult, setApprovalResult] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [fullSsn, setFullSsn] = useState(null);     // fetched on demand
  const [ssnRevealed, setSsnRevealed] = useState(false);

  const revealSsn = async () => {
    if (fullSsn) { setSsnRevealed(true); return; }
    try {
      const r = await api.get(`/admin/agent-applications/${app.id}`);
      setFullSsn(r.data.ssn || "");
      setSsnRevealed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not fetch SSN");
    }
  };

  // Formats 9-digit string to XXX-XX-XXXX for human reading
  const fmtSsn = (s) => {
    const d = (s || "").replace(/\D/g, "");
    return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : s || "";
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/agent-applications/${app.id}/approve`, {
        ...bank, commission_percent: Number(bank.commission_percent),
      });
      setApprovalResult(r.data);
      toast.success("Agent created");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Please provide a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/agent-applications/${app.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Application rejected");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="app-review-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl flex items-center justify-between gap-3">
            {app.full_name}
            {onPrint && (
              <Button size="sm" variant="outline" onClick={onPrint} data-testid="dialog-print-btn">
                <Printer size={14} className="mr-1" /> Print
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            <span className="mono font-semibold text-[#184A30]">{app.email}</span> · {app.phone}
          </DialogDescription>
        </DialogHeader>

        {approvalResult ? (
          <ApprovalSuccess result={approvalResult} onDone={onClose} />
        ) : (
          <>
            <div className="bg-white rounded-xl border border-[#E5E0D8] p-4">
              <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">Identity</p>
              <dl className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <Row k="Full name" v={app.full_name} />
                <Row k="Phone" v={app.phone} mono />
                <Row k="Email" v={app.email} mono highlight />
                <Row k="DL #" v={app.dl_number} mono />
                <div>
                  <dt className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] flex items-center gap-1">
                    SSN <span className="text-rose-600">(sensitive)</span>
                  </dt>
                  <dd className="mono text-[#112318] flex items-center gap-2" data-testid="dialog-ssn">
                    {ssnRevealed
                      ? <span className="font-semibold">{fmtSsn(fullSsn)}</span>
                      : <span>•••-••-{app.ssn_last4 || "????"}</span>}
                    {!ssnRevealed && (
                      <button
                        type="button"
                        onClick={revealSsn}
                        className="text-[10px] tracking-widest uppercase text-[#184A30] underline"
                        data-testid="dialog-ssn-reveal-btn"
                      >
                        Reveal
                      </button>
                    )}
                  </dd>
                </div>
                <Row k="Address" v={app.address} className="col-span-2" />
                {app.why_join && <Row k="Why" v={app.why_join} className="col-span-2" />}
                <Row k="Submitted" v={new Date(app.created_at).toLocaleString()} />
                <Row k="Status" v={app.status} />
              </dl>
            </div>

            <h3 className="serif text-xl mt-4">Driver license</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <DLImage appId={app.id} side="front" />
              <DLImage appId={app.id} side="back" />
            </div>

            {app.ssn_card_path && (
              <>
                <h3 className="serif text-xl mt-4">Social Security card</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <DLImage appId={app.id} side="ssn-card" />
                </div>
              </>
            )}

            {app.status === "pending" && !showRejectForm && (
              <>
                <h3 className="serif text-xl mt-4">Approve · capture bank info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <ApproveField label="Account holder" val={bank.bank_account_holder} on={v => setBank({ ...bank, bank_account_holder: v })} testId="bank-holder" />
                  <ApproveField label="Bank name" val={bank.bank_name} on={v => setBank({ ...bank, bank_name: v })} testId="bank-name" />
                  <ApproveField label="Account number" val={bank.bank_account_number} on={v => setBank({ ...bank, bank_account_number: v })} mono testId="bank-acct" />
                  <ApproveField label="Routing #" val={bank.routing_number} on={v => setBank({ ...bank, routing_number: v })} mono testId="bank-routing" />
                  <ApproveField label="Commission %" type="number" step="0.1" val={bank.commission_percent} on={v => setBank({ ...bank, commission_percent: v })} mono testId="bank-comm-pct" />
                </div>
                <DialogFooter className="mt-4 sm:justify-between flex-row">
                  <Button variant="outline" className="text-rose-700" onClick={() => setShowRejectForm(true)} data-testid="show-reject-btn">
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                  <Button onClick={approve} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="approve-btn">
                    <CheckCircle size={14} className="mr-1" /> {busy ? "Approving…" : "Approve & create agent"}
                  </Button>
                </DialogFooter>
              </>
            )}

            {app.status === "pending" && showRejectForm && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mt-4">
                <p className="text-xs tracking-[0.2em] uppercase text-rose-700 font-semibold">Reject application</p>
                <Label className="mt-3">Reason (will be visible in audit log)</Label>
                <textarea
                  rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  className="w-full mt-1 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                  placeholder="e.g. Driver license photos are blurry — please resubmit"
                  data-testid="reject-reason"
                />
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="ghost" onClick={() => setShowRejectForm(false)}>Cancel</Button>
                  <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-reject-btn">
                    {busy ? "Rejecting…" : "Confirm rejection"}
                  </Button>
                </div>
              </div>
            )}

            {app.status !== "pending" && (
              <div className="bg-stone-100 rounded-xl p-4 mt-4 text-sm">
                Already {app.status} by {app.reviewed_by || "—"} on{" "}
                {app.reviewed_at ? new Date(app.reviewed_at).toLocaleString() : "—"}.
                {app.rejection_reason && <div className="mt-2 text-rose-700"><strong>Reason:</strong> {app.rejection_reason}</div>}
                {app.agent_code && <div className="mt-2 mono">Agent code: <strong>{app.agent_code}</strong></div>}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, mono, highlight, className = "" }) {
  const valueCls = highlight
    ? "text-[#184A30] font-semibold underline decoration-dotted underline-offset-2"
    : "text-[#112318]";
  return (
    <div className={className}>
      <dt className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{k}</dt>
      <dd className={`${valueCls} ${mono ? "mono" : ""}`}>{v || "—"}</dd>
    </div>
  );
}

function ApproveField({ label, val, on, type = "text", step, mono, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type} value={val} onChange={e => on(e.target.value)} step={step}
        className={`w-full mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30] ${mono ? "mono" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}

function DLImage({ appId, side }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    let createdUrl = null;
    (async () => {
      try {
        const r = await api.get(`/admin/agent-applications/${appId}/dl/${side}`, { responseType: "blob" });
        if (cancelled.current) return;
        createdUrl = URL.createObjectURL(r.data);
        setBlobUrl(createdUrl);
      } catch (e) {
        setErr("Could not load image");
      }
    })();
    return () => {
      cancelled.current = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [appId, side]);

  return (
    <div className="bg-stone-50 border border-[#E5E0D8] rounded-xl overflow-hidden" data-testid={`dl-image-${side}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#4A5C50] px-3 py-2 border-b border-[#E5E0D8] flex items-center gap-2">
        <IdentificationCard size={12} weight="duotone" />
        {side === "front" ? "Front" : side === "back" ? "Back" : "SSN card"}
      </div>
      <div className="aspect-[16/10] flex items-center justify-center">
        {blobUrl ? (
          <img src={blobUrl} alt={`Applicant ${side}`} className="max-w-full max-h-full object-contain" />
        ) : err ? (
          <p className="text-xs text-rose-700">{err}</p>
        ) : (
          <p className="text-xs text-[#4A5C50]">Loading…</p>
        )}
      </div>
    </div>
  );
}

function ApprovalSuccess({ result, onDone }) {
  const link = result.manual_set_password_link;
  const copyLink = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    toast.success("Setup link copied");
  };
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="approval-success">
      <CheckCircle size={32} weight="duotone" className="text-emerald-700" />
      <h3 className="serif text-2xl mt-2">Agent created</h3>

      {result.welcome_email_sent ? (
        <p className="text-sm text-[#4A5C50] mt-1" data-testid="approval-email-sent">
          A branded welcome email with their agent code + set-password link (24h expiry) was sent to{" "}
          <span className="mono">{result.applicant_email}</span>. They&apos;ll choose their own password and sign in immediately.
        </p>
      ) : (
        <div data-testid="approval-email-failed">
          <p className="text-sm text-amber-800 mt-1">
            <strong>Email send failed</strong> — please share this one-time setup link with{" "}
            <span className="mono">{result.applicant_email}</span> through another channel
            (SMS, secure messaging). It expires in 24 hours and can only be used once.
          </p>
          {link && (
            <div className="bg-white rounded-lg border border-amber-200 p-3 mt-2 break-all text-xs mono">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#112318]">{link}</span>
                <button type="button" onClick={copyLink} className="text-[#184A30] underline shrink-0" data-testid="copy-setup-link">
                  <Copy size={12} className="inline" /> Copy
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border border-emerald-200 p-3 mt-3">
        <dt className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Agent code</dt>
        <dd className="mono font-semibold text-lg">{result.agent_code}</dd>
      </div>

      <div className="flex justify-end mt-4">
        <Button onClick={onDone} className="bg-[#184A30] text-white">Done</Button>
      </div>
    </div>
  );
}

/**
 * Renders a clean print-friendly HTML page for a single application.
 * Opened in a new tab — the print dialog fires automatically after a short
 * delay so the layout has time to lay out. DL images are intentionally NOT
 * embedded here (they require an authenticated GET, and embedding the raw
 * URL in a window.open page would expose a bearer token). Reviewer can
 * print physical copies of the DL via the Review dialog if needed.
 */
function renderPrintHTML(app) {
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Application — ${esc(app.full_name)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #112318; padding: 32px; max-width: 720px; margin: auto; }
  h1 { font-family: Georgia, serif; font-size: 28px; margin: 0 0 4px; }
  .sub { color: #4A5C50; font-size: 13px; margin-bottom: 24px; }
  .grid { display: grid; grid-template-columns: 180px 1fr; gap: 12px 18px; font-size: 13px; }
  .grid dt { color: #4A5C50; text-transform: uppercase; font-size: 10px; letter-spacing: 0.15em; padding-top: 3px; }
  .grid dd { margin: 0; color: #112318; }
  .mono { font-family: "SF Mono", Menlo, monospace; }
  .highlight { font-weight: 600; }
  .meta { margin-top: 24px; padding-top: 12px; border-top: 1px solid #E5E0D8; color: #4A5C50; font-size: 11px; }
  .pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; font-weight: 600; }
  .pill.pending { background: #FEF3C7; color: #92400E; }
  .pill.approved { background: #D1FAE5; color: #047857; }
  .pill.rejected { background: #FEE2E2; color: #B91C1C; }
  @media print { body { padding: 0; } }
</style></head>
<body>
  <h1>Agent application</h1>
  <p class="sub">Zcardon · printed ${esc(new Date().toLocaleString())}</p>
  <dl class="grid">
    <dt>Status</dt><dd><span class="pill ${esc(app.status)}">${esc(app.status)}</span></dd>
    <dt>Full name</dt><dd class="highlight">${esc(app.full_name)}</dd>
    <dt>Email</dt><dd class="mono highlight">${esc(app.email)}</dd>
    <dt>Phone</dt><dd class="mono">${esc(app.phone)}</dd>
    <dt>Driver license #</dt><dd class="mono">${esc(app.dl_number)}</dd>
    <dt>SSN</dt><dd class="mono">${app.ssn_last4 ? `•••-••-${esc(app.ssn_last4)}` : "—"}</dd>
    <dt>Address</dt><dd>${esc(app.address)}</dd>
    ${app.why_join ? `<dt>Reason to join</dt><dd>${esc(app.why_join)}</dd>` : ""}
    <dt>Submitted</dt><dd>${esc(new Date(app.created_at).toLocaleString())}</dd>
    ${app.reviewed_at ? `<dt>Reviewed</dt><dd>${esc(new Date(app.reviewed_at).toLocaleString())} by ${esc(app.reviewed_by || "—")}</dd>` : ""}
    ${app.rejection_reason ? `<dt>Rejection reason</dt><dd>${esc(app.rejection_reason)}</dd>` : ""}
    ${app.agent_code ? `<dt>Agent code</dt><dd class="mono highlight">${esc(app.agent_code)}</dd>` : ""}
  </dl>
  <p class="meta">Application ID · <span class="mono">${esc(app.id)}</span></p>
</body></html>`;
}
